import React from 'react';

import { useIntl } from 'react-intl';

import { LIST_SPACES_RESOURCE } from 'src/features/spaces/api/useListSpaces';
import { useUpdateSpace } from 'src/features/spaces/api/useUpdateSpace';
import SpaceFormModal from 'src/features/spaces/components/form/SpaceFormModal';
import { Space } from 'src/features/spaces/utils/types';
import { gatewayMessages } from 'src/features/gateway/utils/messages';
import { spacesMessages } from 'src/features/spaces/utils/messages';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import { DataTableNewEntryProps } from 'src/registry/components/table';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { useParking } from 'src/providers/ParkingProvider';

interface UpdateSpaceFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedSpace: Space;
    children?: React.ReactNode;
}

function UpdateSpaceFormModal<TData>({
    className,
    semantics,
    children,
    selectedSpace,
}: UpdateSpaceFormModalProps<TData>) {
    const { formatMessage } = useIntl();
    const { parkingId } = useParking();

    const { mutate, isPending } = useUpdateSpace({
        onSuccess: (success) => {
            void queryClient.invalidateQueries({ queryKey: [LIST_SPACES_RESOURCE] });
            toast({
                title: formatMessage(spacesMessages.updateSpaceSuccess),
                description: formatMessage(spacesMessages.updateSpaceSuccessDescription, {
                    entry: success.number,
                }),
            });
        },
    });

    return (
        <SpaceFormModal
            selectedSpace={selectedSpace}
            className={className}
            mutate={(data, onSuccess, onError) => mutate({ data, spaceId: selectedSpace.id, parkingId }, { onSuccess, onError })}
            title={formatMessage(generalMessages.editEntry, { entry: semantics?.entryName?.toLowerCase() })}
            icon={ICON_MAP.EDIT}
            isPending={isPending}
            subtitle={formatMessage(gatewayMessages.editGatewaySubtitle, {
                entry: selectedSpace.number,
            })}
        >
            {children}
        </SpaceFormModal>
    );
}

export default UpdateSpaceFormModal;
